import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function usePaymentTracking(billId?: string) {
  return useQuery({
    queryKey: ['payment-tracking', billId],
    queryFn: async () => {
      let query = supabase
        .from('payment_tracking')
        .select('*');

      if (billId) {
        query = query.eq('bill_id', billId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });
}

export function usePaymentSchedules(workspaceId: string) {
  return useQuery({
    queryKey: ['payment-schedules', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_schedules')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('is_active', true);

      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId
  });
}

export function useScheduledPayments(billId?: string) {
  return useQuery({
    queryKey: ['scheduled-payments', billId],
    queryFn: async () => {
      let query = supabase
        .from('scheduled_payments')
        .select('*');

      if (billId) {
        query = query.eq('bill_id', billId);
      }

      const { data, error } = await query.order('scheduled_date', { ascending: true });

      if (error) throw error;
      return data;
    }
  });
}

export function useSchedulePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      billId: string;
      workspaceId: string;
      billDate: string;
      dueDate?: string;
      amount: number;
      vendorId?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('payment-scheduler', {
        body: input
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-payments'] });
      queryClient.invalidateQueries({ queryKey: ['payment-tracking'] });
      toast.success(`Payment scheduled for ${data.scheduledDate}`);
    },
    onError: (error: Error) => {
      toast.error('Failed to schedule payment: ' + error.message);
    }
  });
}

export function useUpdatePaymentStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      paymentId, 
      status, 
      paymentReference 
    }: { 
      paymentId: string; 
      status: string; 
      paymentReference?: string 
    }) => {
      const { data, error } = await supabase
        .from('payment_tracking')
        .update({
          payment_status: status,
          payment_reference: paymentReference
        })
        .eq('id', paymentId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-tracking'] });
      toast.success('Payment status updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update status: ' + error.message);
    }
  });
}
